/**
 * Barras "Comissao x custo" (ou "Recuperado x comissao") por dia ou por
 * semana, todas as empresas.
 *
 * Decisoes nao obvias:
 *  1. Sem seletor de agrupamento: ate 14 dias de janela e por dia, acima e
 *     por semana (`agrupar.ts`). A janela e sempre a do cabecalho.
 *  2. Dois modos, com chips: "Comissao x custo" (a pergunta da margem, o
 *     padrao) e "Recuperado x comissao" (a pergunta do volume). Num grafico
 *     so, o recuperado esmagaria comissao e custo -- a comissao e ~15% do
 *     recuperado e o custo e uma fracao disso.
 *  3. Cada medida tem UMA cor na pagina inteira: recuperado = serie 1,
 *     comissao = serie 2, custo = serie 5. Trocar de modo nao troca a cor de
 *     ninguem.
 *  4. Abaixo do grafico, a leitura pronta: a margem do periodo e quantos
 *     baldes fecharam no negativo (custo fixo correndo sem venda).
 *  5. Sem custo medido (motor real, custo `null`), o modo "Comissao x custo"
 *     some e o grafico fica em "Recuperado x comissao": desenhar a barra de
 *     custo em zero seria afirmar que a operacao nao custou nada.
 */

import { useState } from 'react';
import { Badge, BarChart, FilterChips, Footnote, SectionCard, type BarSeries, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { SerieAgrupada } from '../agrupar';

type Modo = 'comissao_custo' | 'recuperado_comissao';

const MODOS: readonly ChipOption<Modo>[] = [
  { value: 'comissao_custo', label: 'Comissão × custo' },
  { value: 'recuperado_comissao', label: 'Recuperado × comissão' },
];

const DESCRICAO: Readonly<Record<Modo, Readonly<Record<SerieAgrupada['granularidade'], string>>>> = {
  comissao_custo: {
    dia: 'Comissão do Converte Fácil e custo da operação, somados por dia, todas as empresas. A diferença é a margem.',
    semana: 'Comissão do Converte Fácil e custo da operação, somados por semana (segunda a domingo), todas as empresas. A diferença é a margem.',
  },
  recuperado_comissao: {
    dia: 'Valor recuperado e comissão do Converte Fácil, somados por dia, todas as empresas.',
    semana: 'Valor recuperado e comissão do Converte Fácil, somados por semana (segunda a domingo), todas as empresas.',
  },
};

export function GraficoComissaoCusto({ serie, className = '' }: { readonly serie: SerieAgrupada; readonly className?: string }): JSX.Element {
  const [escolhido, setModo] = useState<Modo>('comissao_custo');
  const semCusto = serie.baldes.some((b) => b.custoCents === null);
  const modo: Modo = semCusto ? 'recuperado_comissao' : escolhido;
  const unidade = serie.granularidade;
  const custos = serie.custoCents.map((v) => v ?? 0);

  const series: readonly BarSeries[] =
    modo === 'comissao_custo'
      ? [
          { key: 'comissao', label: 'Comissão', values: serie.comissaoCents, tone: 2 },
          { key: 'custo', label: 'Custo da operação', values: custos, tone: 5 },
        ]
      : [
          { key: 'recuperado', label: 'Recuperado (bruto)', values: serie.recuperadoCents, tone: 1 },
          { key: 'comissao', label: 'Comissão', values: serie.comissaoCents, tone: 2 },
        ];

  const margemTotal = serie.margemCents.reduce<number>((acc, v) => acc + (v ?? 0), 0);
  const negativos = serie.baldes.filter((b) => b.custoCents !== null && b.comissaoCents - b.custoCents < 0);
  const titulo = modo === 'comissao_custo' ? `Comissão e custo por ${unidade}` : `Recuperado e comissão por ${unidade}`;

  return (
    <SectionCard
      id="comissao-custo"
      title={titulo}
      icon="moeda"
      description={DESCRICAO[modo][unidade]}
      aside={
        semCusto ? (
          <Badge tone="neutral" icon="info">
            Custo não medido
          </Badge>
        ) : (
          <FilterChips label="Escolher o que comparar" options={MODOS} value={modo} onChange={setModo} />
        )
      }
      className={className}
    >
      <div className="flex flex-col gap-4">
        {serie.baldes.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-subtle">Nenhum valor no período: as barras começam vazias.</p>
        ) : (
          <BarChart labels={serie.labels} series={series} formatValue={formatBRLFromCents} height={220} title={titulo} />
        )}

        {modo === 'comissao_custo' && serie.baldes.length > 0 ? (
          <p className="text-xs text-ink-muted">
            Margem no período: <strong className={`font-semibold tabular-nums ${margemTotal < 0 ? 'text-status-critical' : 'text-ink'}`}>{formatBRLFromCents(margemTotal)}</strong>
            {negativos.length === 0 ? (
              <> · nenhum{unidade === 'dia' ? ' dia' : 'a semana'} fechou no negativo.</>
            ) : (
              <>
                {' '}
                · {formatCount(negativos.length)} {unidade === 'dia' ? (negativos.length === 1 ? 'dia fechou' : 'dias fecharam') : negativos.length === 1 ? 'semana fechou' : 'semanas fecharam'} no
                negativo (custo fixo correndo sem venda): {negativos.map((b) => b.rotulo).join(', ')}.
              </>
            )}
          </p>
        ) : null}

        <Footnote>
          A comissão de cada venda é congelada no percentual da empresa naquele momento (ADR-001).{' '}
          {semCusto ? 'O custo da operação ainda não é medido pelo motor, por isso não há barra de custo.' : 'Custos com tarifas de exemplo na prévia.'} Até 14 dias de janela, uma
          barra por dia; acima disso, por semana.
        </Footnote>
      </div>
    </SectionCard>
  );
}
